import { Button, Card, Pagination, PermissionGate, Table, TableColumn } from '@/components/common';
import { usePermissions } from '@/hooks';
import { DeviceDTO, InstitutionDTO, LabDTO, PaginationMetadata } from '@/models';
import React from 'react';

interface DeviceTableProps {
  devices: DeviceDTO[];
  labs: LabDTO[];
  institutions: InstitutionDTO[];
  pagination: PaginationMetadata | null;
  currentPage: number;
  pageSize: number;
  pageSizeOptions: readonly number[];
  isLoading: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onEdit: (device: DeviceDTO) => void;
  onDelete: (device: DeviceDTO) => void;
}

export const DeviceTable: React.FC<DeviceTableProps> = ({
  devices,
  labs,
  institutions,
  pagination,
  currentPage,
  pageSize,
  pageSizeOptions,
  isLoading,
  onPageChange,
  onPageSizeChange,
  onEdit,
  onDelete,
}) => {
  const { canPerform } = usePermissions();

  const columns: TableColumn<DeviceDTO>[] = [
    {
      key: 'id',
      header: 'ID',
      sortable: true,
    },
    {
      key: 'serialNumber',
      header: 'Serial Number',
      sortable: true,
      render: (device) => device.serialNumber || '-',
    },
    {
      key: 'model',
      header: 'Model',
      render: (device) => device.model || '-',
    },
    {
      key: 'identifier',
      header: 'Identifier',
      render: (device) => device.identifier || '-',
    },
    {
      key: 'lab',
      header: 'Lab',
      render: (device) => {
        const lab = labs.find((l) => l.id === device.labId);
        return lab ? (
          <div className="small">
            <div>{lab.name}</div>
            <div className="text-muted">{lab.id}</div>
          </div>
        ) : (
          '-'
        );
      },
    },
    {
      key: 'institution',
      header: 'Institution',
      render: (device) => {
        const institution = institutions.find((i) => i.id === device.institutionId);
        return institution ? (
          <div className="small">
            <div>{institution.name || '-'}</div>
            <div className="text-muted">{institution.id}</div>
          </div>
        ) : (
          '-'
        );
      },
    },
    {
      key: 'versions',
      header: 'Versions',
      render: (device) => (
        <div className="small">
          {device.firmwareVersion && <div>FW: {device.firmwareVersion}</div>}
          {device.hardwareVersion && <div>HW: {device.hardwareVersion}</div>}
          {!device.firmwareVersion && !device.hardwareVersion && '-'}
        </div>
      ),
    },
    ...(canPerform('device:edit') || canPerform('device:delete')
      ? [
          {
            key: 'actions' as const,
            header: 'Actions',
            width: '150px',
            render: (device: DeviceDTO) => (
              <div className="d-flex gap-2">
                <PermissionGate action="device:edit">
                  <Button
                    variant="primary"
                    size="sm"
                    icon="fas fa-edit"
                    onClick={() => onEdit(device)}
                  >
                    Edit
                  </Button>
                </PermissionGate>
                <PermissionGate action="device:delete">
                  <Button
                    variant="danger"
                    size="sm"
                    icon="fas fa-trash"
                    onClick={() => onDelete(device)}
                  />
                </PermissionGate>
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <Card className="border-0 shadow-sm">
      <Table
        columns={columns}
        data={devices}
        keyExtractor={(device) => device.id}
        isLoading={isLoading}
        emptyMessage="No devices found. Click 'Add Device' to create one."
      />
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div className="d-flex align-items-center gap-2">
          <small className="text-muted">Rows per page:</small>
          <select
            className="form-select form-select-sm"
            style={{ width: 'auto' }}
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
          {pagination && (
            <small className="text-muted">
              Showing {(currentPage - 1) * pageSize + 1}–
              {Math.min(currentPage * pageSize, pagination.totalCount)} of {pagination.totalCount}{' '}
              devices
            </small>
          )}
        </div>
        <Pagination pagination={pagination} onPageChange={onPageChange} />
      </div>
    </Card>
  );
};
